import { PhoneNumberUtil } from 'google-libphonenumber';

const phoneUtil = PhoneNumberUtil.getInstance();

const UA_DIAL_CODE = '380';
const UA_PREFIX = `+${UA_DIAL_CODE}`;
const UA_NATIONAL_LENGTH = 9;

// Український номер: код країни + 9 цифр. Перша з них — код оператора або
// міста, і нуля на цій позиції не буває в жодного оператора. Саме нуль клієнти
// дописують за звичкою від локального запису (067…), тож ця перевірка ловить
// найчастішу помилку вводу і при цьому не обмежує список операторів.
const UA_PHONE = /^\+380[1-9]\d{8}$/;

// ⚠️ Український номер НЕ перевіряємо через google-libphonenumber.
//
// Бібліотека возить із собою ЗНІМОК метаданих (у нас 3.2.34) і не знає серій,
// які оператори отримали пізніше: живий +380 77 505 77 17 вона вважає
// невалідним, і замовлення просто не оформлюється. Перелічити коди операторів
// списком — та сама пастка з відкладеним строком: наступна нова серія зламає
// оформлення знову.
//
// Тому для +380 перевіряємо СТРУКТУРУ — рівно так, як це вже робить бек
// (`models/order.js`: `/^\+380\d{9}$/`). Для решти країн лишається
// libphonenumber: поля зафіксовані на Україні (`defaultCountry="ua"` +
// `forceDialCode`), але функція має лишатись загальною.
export const isPhoneValid = (phone: string): boolean => {
  // Порожнє поле — це сам лише код країни, який підставляє компонент. Помилку
  // тут не показуємо: клієнт ще нічого не ввів. Порожній номер ловить сабміт.
  if (phone === UA_PREFIX) {
    return true;
  }

  if (phone.startsWith(UA_PREFIX)) {
    return UA_PHONE.test(phone);
  }

  try {
    return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));
  } catch (error) {
    return false;
  }
};

// Нуль на початку національної частини.
//
// Маска українського номера (`(..) ... .. ..`) рахує рівно 9 цифр. Клієнт, що
// за звичкою починає з нуля, вводить на цифру більше — і ОСТАННЯ цифра просто
// не влазить у маску. На вигляд номер повний, довжину він проходить, а дзвонить
// менеджер уже не тому абонентові. Тому нуль прибираємо ще під час набору, а не
// на сабміті.
export const stripUaTrunkZero = (phone: string): string => {
  if (!phone.startsWith(UA_PREFIX)) {
    return phone;
  }

  return `${UA_PREFIX}${phone.slice(UA_PREFIX.length).replace(/^0+/, '')}`;
};

// Вставка повного номера в поле — з префіксом (+380 / 380), у локальному
// записі (0XX…) чи взагалі без нічого.
//
// Власне вставка в react-international-phone не прибирає повторний код країни й
// дає зліплене «+380 380634350134», тож нормалізуємо самі й пишемо готове
// значення прямо в стан, минаючи штатне склеювання.
//
// Повертає null, якщо з вставленого тексту не складається український номер —
// тоді обробник не втручається і подія йде своїм шляхом.
export const normalizeUaPhonePaste = (raw: string): string | null => {
  let digits = raw.replace(/\D/g, '');

  if (!digits) {
    return null;
  }

  if (digits.startsWith(UA_DIAL_CODE) && digits.length > UA_DIAL_CODE.length) {
    digits = digits.slice(UA_DIAL_CODE.length);
  }

  // Нуль-транк локального запису (067…) у міжнародний формат не входить.
  digits = digits.replace(/^0+/, '');

  return digits.length === UA_NATIONAL_LENGTH ? `${UA_PREFIX}${digits}` : null;
};
